import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AppRoutes } from '@/constants/appRoutes'
import { useReportData, type ISocialMediaFound } from '@/hooks/useReportData'
import { useReportStream } from '@/hooks/useReportStream'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { ERunReportStep } from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'

import { MainView } from './components/GeneratingReport/MainView'

/**
 * Maps each socialMedia field on the form state to its canonical platform name
 * (matching what the scraper backend emits in `scraper:profile_scraped.platform`).
 */
const SOCIAL_INPUT_TO_PLATFORM: Array<{
  field: keyof ReturnType<typeof selectRunReport>['socialMedia']
  platform: string
}> = [
  { field: 'linkedIn', platform: 'LinkedIn' },
  { field: 'x', platform: 'Twitter' },
  { field: 'facebook', platform: 'Facebook' },
  { field: 'instagram', platform: 'Instagram' },
  { field: 'tikTok', platform: 'TikTok' },
  { field: 'pinterest', platform: 'Pinterest' },
  { field: 'reddit', platform: 'Reddit' },
]

function extractUsernameFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/\/+$/, '')
    const last = path.split('/').filter(Boolean).pop() || ''
    return last.replace(/^@/, '')
  } catch {
    return ''
  }
}

function buildInputProfiles(
  socialMedia: ReturnType<typeof selectRunReport>['socialMedia'],
): ISocialMediaFound[] {
  const now = new Date().toISOString()
  return SOCIAL_INPUT_TO_PLATFORM.flatMap(({ field, platform }) => {
    const url = socialMedia[field]
    if (!url) return []
    return [
      {
        platform,
        userName: extractUsernameFromUrl(url),
        url,
        followers: 0,
        following: 0,
        postsCount: 0,
        timestamp: now,
      },
    ]
  })
}

export const GeneratingReport = () => {
  // URL search params are the source of truth so the page survives refresh and
  // remounts, and supports multiple parallel reports (each with its own URL).
  const [searchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const reportId = searchParams.get('reportId')

  useRunReportGuard(ERunReportStep.GeneratingReport)

  const searchInfo = useSelector(selectRunReport)
  const navigate = useNavigate()

  const { events, isReportCompleted } = useReportStream(subjectId)

  // Seed subject from form input so name + photo render instantly, without
  // waiting for find:subject_identified.
  const inputSubject = useMemo(() => {
    const { firstName, lastName, image } = searchInfo.subjectInfo
    if (!firstName && !lastName) return null
    return {
      firstName,
      lastName,
      imageUrl: typeof image === 'string' && image ? image : undefined,
    }
  }, [searchInfo.subjectInfo])

  // Placeholders (platform + username only) until the real scraper events land.
  // SocialMediaList filters them out by URL once scraper:profile_scraped arrives.
  const inputProfiles = useMemo(
    () => buildInputProfiles(searchInfo.socialMedia),
    [searchInfo.socialMedia],
  )

  const reportData = useReportData(events, inputSubject)

  const [phase, setPhase] = useState<'init' | 'main'>(() =>
    events.some((e) => e.type.startsWith('find:')) ? 'main' : 'init',
  )

  useEffect(() => {
    if (phase === 'init' && events.some((e) => e.type.startsWith('find:'))) {
      setPhase('main')
    }
  }, [events, phase])

  useEffect(() => {
    if (!isReportCompleted) return

    // Small delay so the user sees the completed state before we navigate.
    const timer = setTimeout(() => {
      navigate(AppRoutes.digitalReport, {
        state: { id: reportId, subjectId },
      })
    }, 3000)

    return () => clearTimeout(timer)
  }, [isReportCompleted, reportId, subjectId, navigate])

  return (
    <div className="h-full w-full text-foreground">
      <MainView
        phase={phase}
        events={events}
        reportData={reportData}
        inputProfiles={inputProfiles}
        isReportCompleted={isReportCompleted}
      />
    </div>
  )
}
